import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import type { HealthCard, AnalysisRecord } from '../../hooks/useSellerAnalysis';
import { PlatformIcon } from '../shared/PlatformIcon';
import { Drawer, FlowBoard, HealthBoard, ReviewTag, URGENCY_BADGE, flowStageOf, marketplaceName, type BoardGrouping, type HealthColumn } from './sellerUi';

// Saude dos anuncios: card do quadro, quadro por etapa e painel de diagnostico.
// Antes era uma tela propria ("Saude dos anuncios"); agora e o modo "Quadro" da
// tela Anuncios, e o painel abre tambem a partir da tabela de metricas.

const REVIEW_LABEL: Record<string, string> = {
  pendente: 'Pendente',
  revisado: 'Revisando',
  corrigido: 'Corrigido',
  ignorado: 'Ignorado',
};

// Quadro por saúde: as colunas que a IA usa na análise do anúncio.
export const LISTING_HEALTH_COLUMNS: HealthColumn[] = [
  { id: 'critico', label: 'Crítico', dot: 'bg-red-500', head: 'text-red-700 border-red-200 bg-red-50' },
  { id: 'problema', label: 'Problema', dot: 'bg-orange-500', head: 'text-orange-700 border-orange-200 bg-orange-50' },
  { id: 'atencao', label: 'Atenção', dot: 'bg-amber-400', head: 'text-amber-700 border-amber-200 bg-amber-50' },
  { id: 'saudavel', label: 'Saudável', dot: 'bg-emerald-500', head: 'text-emerald-700 border-emerald-200 bg-emerald-50' },
  { id: 'sem_dados', label: 'Sem dados', dot: 'bg-gray-400', head: 'text-gray-600 border-gray-200 bg-gray-50' },
];

// Colunas de saude do backend que nao pedem acao.
const OK_COLUMNS = ['saudavel'];
const WAITING_COLUMNS = ['sem_dados'];

export function listingFlowStage(card: HealthCard) {
  return flowStageOf(card, OK_COLUMNS, WAITING_COLUMNS);
}

function copyValue(e: { stopPropagation: () => void; preventDefault: () => void }, label: string, value: string) {
  e.stopPropagation();
  e.preventDefault();
  navigator.clipboard?.writeText(value).then(
    () => toast.success(`${label} copiado`),
    () => toast.error('Não foi possível copiar'),
  );
}

function CopyChip({ label, value }: { label: string; value: string }) {
  return (
    <span
      role="button"
      tabIndex={0}
      onClick={(e) => copyValue(e, label, value)}
      title={`${label}: ${value} (clique para copiar)`}
      className="inline-flex min-w-0 max-w-full cursor-pointer items-center gap-0.5 whitespace-nowrap rounded px-1 py-0.5 hover:bg-gray-100 hover:text-gray-700"
    >
      <span className="shrink-0">{label}:</span>
      <span className="max-w-[90px] truncate">{value}</span>
    </span>
  );
}

/** Etiqueta de saude usada na tabela de metricas. */
export function HealthBadge({ card, onClick }: { card?: HealthCard; onClick: () => void }) {
  if (!card) {
    return <button type="button" onClick={onClick} className="text-[10px] text-gray-400 hover:text-gray-600">não analisado</button>;
  }
  const urg = URGENCY_BADGE[card.urgency] || URGENCY_BADGE.nenhuma;
  const stage = listingFlowStage(card);
  return (
    <button type="button" onClick={onClick} title={card.headline || card.stateLabel} className="inline-flex flex-col items-start gap-0.5 text-left">
      <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${urg.cls}`}>{urg.label}</span>
      <span className="text-[10px] text-gray-500">{stage === 'revisando' ? 'revisando' : stage === 'aguardando' ? 'aguardando efeito' : card.stateLabel}</span>
    </button>
  );
}

export function ListingHealthCard({ card, onClick }: { card: HealthCard; onClick: () => void }) {
  const urg = URGENCY_BADGE[card.urgency] || URGENCY_BADGE.nenhuma;
  const inCooldown = card.cooldownUntil && new Date(card.cooldownUntil) > new Date();
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onClick(); }}
      className="w-full cursor-pointer rounded-lg border border-gray-200 bg-white p-3 text-left shadow-sm transition hover:border-orange-300 hover:shadow"
    >
      <div className="flex gap-2.5">
        {card.imageUrl ? <img src={card.imageUrl} alt="" className="h-11 w-11 shrink-0 rounded-md object-cover" /> : <div className="h-11 w-11 shrink-0 rounded-md bg-gray-100" />}
        <div className="min-w-0 flex-1">
          <p className="line-clamp-2 text-xs font-medium text-gray-800" title={card.title || ''}>{card.title || card.listingIdExt}</p>
          <div className="mt-0.5 flex flex-wrap items-center gap-x-1 text-[9px] text-gray-400">
            {card.sku && <CopyChip label="SKU" value={card.sku} />}
            <CopyChip label="ID" value={card.listingIdExt} />
          </div>
        </div>
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-1">
        <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${urg.cls}`}>{urg.label}</span>
        <span className="max-w-full truncate rounded bg-gray-100 px-1.5 py-0.5 text-[10px] text-gray-600">{card.stateLabel}</span>
        {card.analysisNumber > 1 && <span className="rounded bg-purple-100 px-1.5 py-0.5 text-[10px] text-purple-700">{card.analysisNumber}ª análise</span>}
        <ReviewTag reviewStatus={card.reviewStatus} cooldownUntil={card.cooldownUntil} />
      </div>
      {card.headline && <p className="mt-1.5 line-clamp-2 text-[11px] text-gray-600">{card.headline}</p>}
      <div className="mt-2 flex items-center gap-1.5 text-[10px]">
        <span className="inline-flex items-center rounded bg-gray-50 p-0.5" title={marketplaceName(card.marketplace)}><PlatformIcon platform={card.marketplace} size={13} /></span>
        {card.marketplace === 'mercadolivre'
          ? <span className="text-gray-500">Visitas {(card.metrics?.visitors ?? 0).toLocaleString('pt-BR')}</span>
          : <span className="text-gray-500">CTR {card.metrics?.ctr ?? 0}%</span>}
        <span className="text-gray-500">· Conv {card.metrics?.cvr ?? 0}%</span>
        {inCooldown && <span className="ml-auto text-amber-600">até {new Date(card.cooldownUntil!).toLocaleDateString('pt-BR')}</span>}
      </div>
    </div>
  );
}

export function ListingsBoard({ cards, onOpen, onMove, grouping = 'etapa' }: { cards: HealthCard[]; onOpen: (card: HealthCard) => void; onMove: (card: HealthCard, reviewStatus: string) => void; grouping?: BoardGrouping }) {
  if (grouping === 'saude') {
    return <HealthBoard items={cards} columns={LISTING_HEALTH_COLUMNS} renderCard={(card) => <ListingHealthCard card={card} onClick={() => onOpen(card)} />} />;
  }
  return (
    <FlowBoard
      items={cards}
      stageOf={listingFlowStage}
      renderCard={(card) => <ListingHealthCard card={card} onClick={() => onOpen(card)} />}
      onMove={(card, _stage, reviewStatus) => onMove(card, reviewStatus)}
      emptyHint="Nenhum anúncio com problema pendente"
    />
  );
}

function num(v: any) {
  if (v === null || v === undefined) return '-';
  return typeof v === 'number' ? v.toLocaleString('pt-BR') : String(v);
}

function Section({ title, items, ordered }: { title: string; items: string[]; ordered?: boolean }) {
  if (!items || items.length === 0) return null;
  const List: any = ordered ? 'ol' : 'ul';
  return (
    <div>
      <p className="mb-1 text-xs font-semibold text-gray-700">{title}</p>
      <List className={`${ordered ? 'list-decimal' : 'list-disc'} space-y-0.5 pl-4 text-[11px] text-gray-600`}>
        {items.map((it, i) => <li key={i}>{it}</li>)}
      </List>
    </div>
  );
}

export function ListingDiagnosisDrawer({
  listingIdExt,
  fallbackTitle,
  onClose,
  fetchHistory,
  reviewAnalysis,
  deleteHistory,
  analysisEnabled,
  onToggleAnalysis,
}: {
  listingIdExt: string;
  fallbackTitle?: string | null;
  onClose: () => void;
  fetchHistory: (id: string) => Promise<AnalysisRecord[]>;
  reviewAnalysis: (id: string, patch: any) => Promise<any>;
  deleteHistory?: (id: string) => Promise<any>;
  analysisEnabled?: boolean;
  onToggleAnalysis?: (enabled: boolean) => void;
}) {
  const [history, setHistory] = useState<AnalysisRecord[] | null>(null);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setHistory(null);
    fetchHistory(listingIdExt).then(setHistory);
  }, [listingIdExt, fetchHistory]);

  const current = history?.[0];

  const doReview = async (reviewStatus: string) => {
    if (!current) return;
    setSaving(true);
    try {
      const res = await reviewAnalysis(current.id, { reviewStatus, reviewNotes: notes || undefined });
      if (res && res.success === false) throw new Error(res.message || 'Falha ao salvar revisão');
      toast.success(reviewStatus === 'corrigido' ? 'Marcado como corrigido. A IA reavalia depois do cooldown.' : 'Revisão salva');
      setNotes('');
      setHistory(await fetchHistory(listingIdExt));
    } catch (err: any) {
      toast.error(err?.message || 'Falha ao salvar revisão');
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    if (!deleteHistory) return;
    if (!confirm('Excluir todo o histórico de análises deste anúncio? Essa ação não pode ser desfeita.')) return;
    const res = await deleteHistory(listingIdExt);
    if (res?.success) { toast.success('Histórico do anúncio excluído'); onClose(); }
    else toast.error(res?.message || 'Falha ao excluir');
  };

  const d = current?.diagnosis;
  const m = current?.metrics;

  return (
    <Drawer
      open
      onClose={onClose}
      title={current?.title || fallbackTitle || listingIdExt}
      subtitle={current ? `${marketplaceName(current.marketplace)} · ${current.stateLabel} · confiança ${current.confidence} · analisado em ${new Date(current.analyzedAt).toLocaleString('pt-BR')}` : `ID ${listingIdExt}`}
      footer={current ? (
        <div className="flex flex-wrap items-center gap-2">
          {current.reviewStatus !== 'revisado' && <button disabled={saving} onClick={() => doReview('revisado')} className="rounded-md bg-sky-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-sky-700 disabled:opacity-50">Estou revisando</button>}
          {current.reviewStatus !== 'corrigido' && <button disabled={saving} onClick={() => doReview('corrigido')} className="rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-emerald-700 disabled:opacity-50">Corrigi</button>}
          {current.reviewStatus !== 'ignorado' && <button disabled={saving} onClick={() => doReview('ignorado')} className="rounded-md border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-50">Ignorar</button>}
          {current.reviewStatus !== 'pendente' && <button disabled={saving} onClick={() => doReview('pendente')} className="rounded-md border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-50">Voltar pra revisar</button>}
          {current.listingUrl && (
            <a href={current.listingUrl} target="_blank" rel="noopener noreferrer" className="ml-auto inline-flex items-center gap-1 rounded-md border border-orange-700 px-3 py-1.5 text-xs font-medium text-orange-700 hover:bg-orange-50">
              Abrir anúncio ↗
            </a>
          )}
        </div>
      ) : undefined}
    >
      {history === null ? (
        <div className="py-10 text-center text-sm text-gray-500">Carregando diagnóstico...</div>
      ) : !current ? (
        <div className="space-y-3 py-6 text-center">
          <p className="text-sm font-medium text-gray-700">Este anúncio ainda não foi analisado pela IA.</p>
          <p className="text-xs text-gray-500">Use "Analisar com IA" no topo da tela. Só entram anúncios com a análise ligada.</p>
          {onToggleAnalysis && (
            <button type="button" onClick={() => onToggleAnalysis(!analysisEnabled)} className={`rounded-full px-3 py-1 text-xs font-semibold ${analysisEnabled ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-600'}`}>
              Análise com IA: {analysisEnabled ? 'ligada' : 'desligada'}
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-2 text-[11px]">
            <span className={`rounded px-1.5 py-0.5 font-semibold ${(URGENCY_BADGE[current.urgency] || URGENCY_BADGE.nenhuma).cls}`}>{(URGENCY_BADGE[current.urgency] || URGENCY_BADGE.nenhuma).label}</span>
            <span className="text-gray-500">Status: <span className="font-medium text-gray-700">{REVIEW_LABEL[current.reviewStatus] || current.reviewStatus}</span></span>
            {current.reviewedBy && <span className="text-gray-400">por {current.reviewedBy}</span>}
            {onToggleAnalysis && (
              <button type="button" onClick={() => onToggleAnalysis(!analysisEnabled)} className={`ml-auto rounded-full px-2 py-0.5 font-semibold ${analysisEnabled ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>
                IA {analysisEnabled ? 'ligada' : 'desligada'}
              </button>
            )}
          </div>

          {d?.source === 'fallback' && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3">
              <p className="text-xs font-semibold text-red-800">Diagnóstico gerado pelo motor (sem IA)</p>
              <p className="mt-0.5 text-[11px] text-red-600">A análise do agente falhou e caiu no fallback determinístico.{d.llmError ? ` Erro: ${d.llmError}` : ''}</p>
            </div>
          )}

          {d?.headline && (
            <div className="rounded-lg bg-orange-50 p-3">
              <div className="flex items-center gap-2">
                <p className="text-sm font-medium text-orange-900">{d.headline}</p>
                {d.source === 'llm' && <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-[9px] font-semibold text-emerald-700">IA</span>}
              </div>
              {d.motivoDoMotor && <p className="mt-1 text-xs text-orange-700">{d.motivoDoMotor}</p>}
            </div>
          )}

          {d && <Section title="O que fazer" items={d.acoes} ordered />}

          {m && (
            <div className="grid grid-cols-3 gap-2 sm:grid-cols-6">
              {[
                ['Impressões', num(m.impressions)],
                ['CTR', `${m.ctr}%`],
                ['Visitas', num(m.visitors)],
                ['Conversão', `${m.cvr}%`],
                ['Pedidos', num(m.orders)],
                ['Receita', `R$ ${num(m.revenue)}`],
                ['ACOS', `${m.adAcos}%`],
                ['TACOS', `${m.tacos}%`],
                ['ROAS', num(m.adRoas)],
                ['Nota', m.ratingAverage != null ? `${m.ratingAverage} (${num(m.reviewsCount)})` : '-'],
                ['Fotos', num(m.photoCount)],
                ['Vídeos', num(m.videoCount)],
              ].map(([label, val]) => (
                <div key={label as string} className="rounded-md border border-gray-100 bg-gray-50 p-2 text-center">
                  <p className="text-[10px] uppercase text-gray-400">{label}</p>
                  <p className="text-xs font-semibold text-gray-800">{val}</p>
                </div>
              ))}
            </div>
          )}

          {d && (
            <div className="grid gap-4 sm:grid-cols-2">
              <Section title="Evidências" items={d.evidencias} />
              <Section title="Hipóteses prováveis" items={d.hipoteses} />
              <Section title="Verificar manualmente" items={d.verificarManualmente} />
            </div>
          )}

          {d?.testeSugerido && (d.testeSugerido.mudanca || d.testeSugerido.metricaAlvo) && (
            <div className="rounded-lg border border-sky-100 bg-sky-50 p-3 text-xs text-sky-800">
              <span className="font-semibold">Teste sugerido:</span> {d.testeSugerido.mudanca}
              {d.testeSugerido.metricaAlvo && <> · alvo: {d.testeSugerido.metricaAlvo}</>}
              {d.testeSugerido.janela && <> · janela: {d.testeSugerido.janela}</>}
            </div>
          )}

          {current.visual && (
            <div className="rounded-lg border border-purple-100 bg-purple-50 p-3">
              <p className="text-xs font-semibold text-purple-900">Parecer da imagem ({current.visual.tipo}) · produto ocupa {current.visual.produtoPercentualFrame} do quadro</p>
              <p className="mt-1 text-xs text-purple-800">{current.visual.classificacao}</p>
              {current.visual.scores && (
                <div className="mt-2 flex flex-wrap gap-2">
                  {Object.entries(current.visual.scores).map(([k, v]) => <span key={k} className="rounded bg-white px-2 py-0.5 text-[10px] text-purple-700">{k}: {v}/10</span>)}
                </div>
              )}
              {Array.isArray(current.visual.melhorias) && current.visual.melhorias.length > 0 && (
                <ul className="mt-2 list-disc pl-4 text-[11px] text-purple-800">{current.visual.melhorias.map((mm, i) => <li key={i}>{mm}</li>)}</ul>
              )}
            </div>
          )}

          <div className="rounded-lg border border-gray-200 p-3">
            <p className="text-xs font-semibold text-gray-700">Anotação</p>
            {current.reviewStatus !== 'pendente' && current.reviewNotes && (
              <p className="mt-1 whitespace-pre-wrap rounded-md bg-gray-50 p-2 text-[11px] text-gray-700">{current.reviewNotes}</p>
            )}
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="O que você mudou? (opcional, vai junto com o status)"
              className="mt-2 w-full rounded-md border border-gray-200 p-2 text-xs focus:border-orange-400 focus:outline-none focus:ring-1 focus:ring-orange-300"
              rows={2}
            />
            <p className="mt-1 text-[10px] text-gray-400">"Corrigi" inicia o cooldown: a IA só reavalia depois que a mudança tiver tempo de fazer efeito.</p>
          </div>

          {history && history.length > 1 && (
            <div>
              <p className="mb-1.5 text-xs font-semibold text-gray-700">Histórico de análises</p>
              <div className="space-y-1">
                {history.map((h) => (
                  <div key={h.id} className="rounded-md border border-gray-100 px-2.5 py-1.5 text-[11px]">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">{new Date(h.analyzedAt).toLocaleString('pt-BR')} · {h.stateLabel}</span>
                      <span className="text-gray-500">{REVIEW_LABEL[h.reviewStatus]}{h.reviewStatus !== 'pendente' && h.reviewedAt ? ` · ${new Date(h.reviewedAt).toLocaleDateString('pt-BR')}` : ''}</span>
                    </div>
                    {h.reviewStatus !== 'pendente' && h.reviewNotes && <p className="mt-1 whitespace-pre-wrap text-[10px] text-gray-500">"{h.reviewNotes}"</p>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {deleteHistory && (
            <button type="button" onClick={doDelete} className="text-[11px] font-medium text-red-600 hover:underline">Excluir histórico de análises deste anúncio</button>
          )}
        </div>
      )}
    </Drawer>
  );
}
